import Link from '@v2/shared/components/Link';
import NamedIcon from '@v2/shared/components/NamedIcon';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { Category } from '@v2/shared/lib/types';

type Props = {
  category: Category;
  /**
   * Lead with the category's thumbnail (jss_sections.thumbnail) instead of its icon,
   * and set the label beside it rather than under it.
   *
   * For a tree whose categories are merchandised with artwork — Gifts & More — where a
   * cover photograph says more than a glyph. A category with no thumbnail set falls
   * back to the icon, so a half-merchandised tree still reads as one band rather than
   * as a row of holes.
   */
  variant?: 'icon' | 'image';
};

/**
 * The icon name comes from the data (jss_sections.icon), so it goes through NamedIcon
 * rather than FontAwesomeIcon directly. Icons register asynchronously; resolving a
 * data-driven name against a set that has not loaded yet returns null and leaves a hole
 * where the glyph should be. NamedIcon draws a fallback and redraws once they arrive.
 * headless rendered `<FontAwesomeIcon icon={['fal', category.icon]} />` here, which is
 * the version that silently renders nothing.
 *
 * A category's `href` comes from the serializer — Category::getHref() is a virtual
 * property — rather than being built here. It arrives *relative* though, because the
 * payload it travels in is cached for a day and an absolute url would pin a host into
 * it, so the base url is the client's to re-attach: on AU, which serves from
 * https://www.discountmags.com/au/, rendering the bare path drops the /au/ segment and
 * sends the visitor to the .com storefront. Hence buildUrl, as every other category
 * link does (the navigation's CategoryLink, CategoryCard and CategoriesDialog).
 */
export default function CategoryItem({ category, variant = 'icon' }: Props) {
  const { buildUrl } = useUrlGenerator();

  if (variant === 'image') {
    return (
      <li className="flex">
        <Link
          href={buildUrl(category.href)}
          className="flex flex-row items-center gap-3 hover:bg-slate-100 active:bg-slate-200 py-2 px-3 w-full border-[1.5px] border-slate-300 rounded-md"
        >
          {category.thumbnail ? (
            <img
              src={category.thumbnail.url}
              alt=""
              width={category.thumbnail.width}
              height={category.thumbnail.height}
              loading="lazy"
              // Fixed box, not the file's own shape: the thumbnails are product covers
              // of assorted proportions and a row of them at intrinsic size reads as a
              // broken grid.
              className="shrink-0 w-11 h-14 object-cover rounded-sm bg-slate-100"
            />
          ) : (
            <span className="shrink-0 w-11 h-14 flex items-center justify-center text-3xl text-slate-600">
              <NamedIcon name={category.icon} />
            </span>
          )}
          <span className="font-semibold text-sm leading-tight">{category.name}</span>
        </Link>
      </li>
    );
  }

  return (
    <li className="flex flex-row md:flex-col items-center justify-center">
      <Link
        href={buildUrl(category.href)}
        className="hover:bg-slate-100 active:bg-slate-200 py-3 px-3 text-center w-full border-[1.5px] border-slate-300 rounded-md"
      >
        <span className="inline-block text-3xl md:text-4xl lg:text-5xl mb-1 mr-2 md:mb-3 md:mr-0">
          <NamedIcon name={category.icon} />
        </span>
        <div className="font-semibold text-center text-xs truncate uppercase tracking-wider max-w-full">
          {category.name}
        </div>
      </Link>
    </li>
  );
}
